import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, User, ArrowRight, BookOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GH", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

function readingTime(body = "") {
  const words = body.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tag, setTag] = useState("all");

  useEffect(() => {
    axios.get(`${API}/blog`)
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const allTags = useMemo(() => {
    const s = new Set();
    posts.forEach((p) => (p.tags || []).forEach((t) => s.add(t)));
    return ["all", ...Array.from(s)];
  }, [posts]);

  const filtered = tag === "all" ? posts : posts.filter((p) => (p.tags || []).includes(tag));
  const [featured, ...rest] = filtered;

  return (
    <div data-testid="blog-page" className="bg-white">
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <img
          src="/journal-hero.webp"
          alt="Ghanaian woman by the Ghana flag — Faytex Journal"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-40 text-white">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/20 px-4 py-1.5 text-xs uppercase tracking-widest font-semibold">
            <BookOpen className="w-3.5 h-3.5" /> The Faytex Journal
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl mt-6 tracking-tighter max-w-2xl">
            Stories from <span className="text-faytex-pink">every flow.</span>
          </h1>
          <p className="mt-5 text-lg text-white/85 max-w-xl leading-relaxed">
            Menstrual health, brand heritage, factory life, and the women of Ghana who make Faytex
            possible.
          </p>
        </div>
      </section>

      {/* TAG FILTER */}
      {allTags.length > 1 && (
        <div data-testid="blog-tags" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 flex flex-wrap justify-center gap-2">
          {allTags.map((t) => (
            <button
              key={t}
              data-testid={`blog-tag-${t}`}
              onClick={() => setTag(t)}
              className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest font-semibold transition-colors ${
                tag === t
                  ? "bg-faytex-pink text-white"
                  : "bg-faytex-pinkLight/60 text-faytex-pinkHover hover:bg-faytex-pink hover:text-white"
              }`}
            >
              {t === "all" ? "All stories" : t}
            </button>
          ))}
        </div>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        {loading ? (
          <p className="text-center text-sm text-faytex-muted py-24">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-faytex-muted py-24">No stories match this filter yet.</p>
        ) : (
          <>
            {/* FEATURED */}
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                data-testid={`blog-featured-${featured.slug}`}
                className="group grid lg:grid-cols-12 gap-8 items-center rounded-3xl overflow-hidden mb-16"
              >
                <div className="lg:col-span-7 relative aspect-[16/10] rounded-3xl overflow-hidden bg-faytex-pinkLight/40">
                  {featured.cover_image && (
                    <img
                      src={resolveImg(featured.cover_image)}
                      alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                    />
                  )}
                  <span className="absolute top-5 left-5 rounded-full bg-faytex-pink text-white text-[11px] uppercase tracking-widest font-semibold px-3 py-1.5">
                    Featured
                  </span>
                </div>
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest text-faytex-muted">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(featured.published_at)}</span>
                    <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {featured.author}</span>
                    <span>· {readingTime(featured.body)} min read</span>
                  </div>
                  <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-4 tracking-tight leading-[1.05] group-hover:text-faytex-pink transition-colors">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-faytex-muted leading-relaxed line-clamp-4">{featured.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-faytex-ink group-hover:bg-faytex-pink text-white px-5 py-2.5 text-sm font-semibold transition-colors">
                    Read the story <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            )}

            {/* GRID */}
            {rest.length > 0 && (
              <div data-testid="blog-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {rest.map((p, i) => (
                  <Link
                    key={p.id}
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group rounded-3xl overflow-hidden bg-white border border-gray-100 shadow-soft hover:shadow-hover transition-all flex flex-col"
                  >
                    <div className="h-1.5 w-full bg-faytex-pink" style={{ opacity: 0.6 + (i % 3) * 0.15 }} />
                    <div className="aspect-[16/10] bg-faytex-pinkLight/40 overflow-hidden">
                      {p.cover_image && (
                        <img
                          src={resolveImg(p.cover_image)}
                          alt={p.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      )}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-faytex-muted">
                        <span>{fmtDate(p.published_at)}</span>
                        <span>·</span>
                        <span>{readingTime(p.body)} min read</span>
                      </div>
                      <h3 className="font-heading text-2xl mt-3 group-hover:text-faytex-pink transition-colors leading-tight">
                        {p.title}
                      </h3>
                      <p className="text-sm text-faytex-muted mt-2 line-clamp-3">{p.excerpt}</p>
                      <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-faytex-pink">
                        Read more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
